import type { ReactNode, Ref } from "react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { cn } from "@/lib/utils"

export type EditToolbarButtonProps = {
  children: ReactNode
  active?: boolean
  title: string
  /** "tooltip" uses ActionHintTooltip and omits the native title attribute. */
  hint?: "tooltip"
  disabled?: boolean
  onClick: () => void
  ref?: Ref<HTMLButtonElement>
  className?: string
}

/** Compact icon toolbar button — shared by Sketch edit toolbar and Location Scene 3D. */
export function EditToolbarButton({
  children,
  active,
  title,
  hint,
  disabled,
  onClick,
  ref,
  className,
}: EditToolbarButtonProps) {
  const button = (
    <button
      ref={ref}
      type="button"
      title={hint === "tooltip" ? undefined : title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md text-ui-text-secondary outline-none",
        "hover:bg-ui-surface-raised hover:text-ui-text-primary disabled:pointer-events-none disabled:opacity-40",
        "focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)]",
        active
          ? "bg-ui-accent/15 text-ui-text-primary ring-1 ring-ui-accent ring-inset"
          : "ring-1 ring-transparent ring-inset",
        className,
      )}
    >
      {children}
    </button>
  )

  if (hint === "tooltip") return <ActionHintTooltip label={title}>{button}</ActionHintTooltip>
  return button
}
